import { ID } from '@ianesthesie-kit/services';
import { doctorBffApi, InvalidateArr } from '../doctorBffApi.slice';
import { wrapError } from '../error';
import {
  DeskPayment,
  DeskPaymentPreview,
  ResolveInterventionOutcomePayload,
  StartDeskPaymentPayload
} from './deskPayment.model';
import { DeskPaymentService } from './deskPayment.service';

/** The payments and the audit trail of one line, both invalidated whenever the other is. */
const lineTags = (lineId: ID | null): InvalidateArr =>
  lineId
    ? [
        { type: 'BillingPayments', id: lineId },
        { type: 'BillingLineEvents', id: lineId }
      ]
    : [];

/**
 * Where a line's card-payment state is read from, invalidated whenever a payment starts
 * mattering to its line or stops doing so — started, canceled, declined — with no money having
 * moved. The chase dashboard is in there because its rows carry the line's own payment and the
 * server-side lock derived from it, so they go stale on exactly the events the patient-scoped
 * payment list does.
 */
export const deskPaymentStateTags = (): InvalidateArr => [
  'DeskPayments',
  'BillingDashboard'
];

/**
 * Everything a desk payment changes outside the payment itself when it collects or gives back
 * money: a line turns PAID (or EXEMPT) server-side, with its payment row and its audit events,
 * so the PDV block and the chase views are stale the moment the outcome is known. Dispatched
 * through `doctorBffApi.util.invalidateTags` for a capture — which happens on Stripe's side,
 * not in a mutation of ours, so there is no `invalidatesTags` to hang it on — and hung on the
 * outcome mutation, which is one of ours.
 *
 * Both of the payment's lines are covered: the line-scoped caches are narrowed to the lines a
 * payment can touch, as every line mutation does, while the rest genuinely span the patient /
 * the hospital. Neither line is optional to include — the two-bite path records
 * STRIPE_AUTHORIZED on the acte line and pays it on the outcome, an acte-only payment has no
 * consultation line at all, and both stay silent for the lines they leave alone.
 */
export const deskPaymentCaptureTags = (payment: DeskPayment): InvalidateArr => [
  'BillingLines',
  ...lineTags(payment.consultationLineId),
  ...lineTags(payment.acteLineId),
  'BillingSummary',
  ...deskPaymentStateTags()
];

/**
 * Caches changed when patient-list polling discovers a renewed head or deferred outcome.
 * Renewal may resolve an acte line by surgery without attaching it to the payment DTO, so its
 * audit trail must be invalidated globally in addition to any line ids the response does name.
 */
export const deskPaymentObservedBillingTags = (
  payment: DeskPayment
): InvalidateArr => [...deskPaymentCaptureTags(payment), 'BillingLineEvents'];

export const deskPaymentApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // The recap must never show yesterday's amounts: the cache entry is dropped as soon as the
    // dialog closes, so a reopened dialog loads instead of flashing a stale total. `BillingLines`
    // covers the rest — a re-priced acte or an amount override changes what this preview says.
    deskPaymentPreview: builder.query<DeskPaymentPreview, { lineId: ID }>({
      keepUnusedDataFor: 0,
      providesTags: (res, err, { lineId }) =>
        err ? [] : [{ type: 'DeskPayments', id: lineId }, 'BillingLines'],
      queryFn: ({ lineId }) =>
        DeskPaymentService.fetchPreview(lineId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    startDeskPayment: builder.mutation<
      DeskPayment,
      { lineId: ID } & StartDeskPaymentPayload
    >({
      invalidatesTags: (res, err) => (err ? [] : deskPaymentStateTags()),
      queryFn: ({ lineId, ...payload }) =>
        DeskPaymentService.start(lineId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    /**
     * A query over a POST: `/refresh` re-reads Stripe and advances the state machine. Webhooks
     * are wired since 3c and can advance the very same row off Stripe's own events, but the
     * browser has no channel of its own to be told when that happens — polling is still how the
     * dialog learns of an outcome either way, so it has to be pollable, which only queries are
     * (`pollingInterval`). Arg-scoped, so one payment's poll is never answered from another's
     * cache.
     *
     * Deliberately outside the tag graph, alone among the desk-payment reads: a tag is how a
     * cache is told it has gone stale, while this entry is the poll's own channel — it is
     * re-read when somebody is waiting on the payment (`pollingInterval`,
     * `refetchOnMountOrArgChange`) and at no other time. Tagged, it would answer the very
     * invalidations its own outcome dispatches — `deskPaymentRegistry.listener.ts` runs
     * synchronously on the response, while the poll that reported it is still subscribed — with
     * one more `POST /refresh` on a payment that is already over, and that round would announce
     * the outcome all over again.
     */
    deskPayment: builder.query<DeskPayment, { paymentIntentId: ID }>({
      queryFn: ({ paymentIntentId }) =>
        DeskPaymentService.refresh(paymentIntentId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    /**
     * Settles or releases a two-bite hold. Unlike a capture, this one *is* a mutation of ours,
     * so it carries the invalidation itself — and it reaches further than a start: the acte
     * line is paid (or waived) by it, which moves its balance, its payments and its trail.
     *
     * `settleLineId` is the acte line the caller priced the capture against, and it is
     * invalidated on top of what the response names: medical-synthesis attaches that line to
     * the payment at the outcome, and a response serialized before the attach would otherwise
     * leave the very line that was just paid showing its old balance and trail.
     */
    resolveInterventionOutcome: builder.mutation<
      DeskPayment,
      {
        paymentIntentId: ID;
        settleLineId?: ID;
      } & ResolveInterventionOutcomePayload
    >({
      // A refusal still re-reads the payments: the hold may have been resolved from another
      // desk — which is what `E1-12-2-1075` reports — and nothing polls a PARTIALLY_CAPTURED
      // row, so the pill and the outcome button would keep offering a hold that is gone.
      invalidatesTags: (payment, err, { settleLineId }) =>
        !err && payment
          ? [
              ...deskPaymentCaptureTags(payment),
              ...lineTags(settleLineId ?? null)
            ]
          : deskPaymentStateTags(),
      queryFn: ({ paymentIntentId, outcome, amount }) =>
        DeskPaymentService.resolveInterventionOutcome(paymentIntentId, {
          outcome,
          amount
        })
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    // Tagged even though the poll's listener invalidates the same thing on an abandonment
    // (`deskPaymentRegistry.listener.ts`): a cancel is not a `/refresh`, and the dialog that
    // ran it is often closed on it, so this mutation is the only witness — and the row's own
    // list must not keep offering to resume a dead payment.
    cancelDeskPayment: builder.mutation<DeskPayment, { paymentIntentId: ID }>({
      invalidatesTags: (res, err) => (err ? [] : deskPaymentStateTags()),
      queryFn: ({ paymentIntentId }) =>
        DeskPaymentService.cancel(paymentIntentId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    deskPaymentsByPatient: builder.query<DeskPayment[], { patientId: ID }>({
      providesTags: (res, err, { patientId }) =>
        err ? [] : [{ type: 'DeskPayments', id: patientId }, 'DeskPayments'],
      queryFn: ({ patientId }) =>
        DeskPaymentService.fetchByPatient(patientId)
          .then((data) => ({ data }))
          .catch(wrapError)
    })
  })
});

export const {
  useDeskPaymentPreviewQuery,
  useStartDeskPaymentMutation,
  useDeskPaymentQuery,
  useLazyDeskPaymentQuery,
  useResolveInterventionOutcomeMutation,
  useCancelDeskPaymentMutation,
  useDeskPaymentsByPatientQuery
} = deskPaymentApi;
